import { usePage } from "@inertiajs/react"
import type { ReactNode } from "react"

import { DashboardLayout } from "@/layouts/dashboard-layout"
import PublicLayout from "@/layouts/public-layout"
import type { SharedData } from "@/types"

interface AuthedShellProps {
  children: ReactNode
  /**
   * Applies to the anonymous (public) branch only — DashboardLayout has no
   * header variant. Defaults to the light header the content surfaces use.
   */
  headerVariant?: "brand" | "light"
}

/**
 * Dual shell by auth. Signed-in visitors get the app rail (DashboardLayout);
 * anonymous visitors keep the public header/footer (PublicLayout). Pages that
 * are viewable both signed-in and signed-out wrap their body in this so the
 * rail follows the reader instead of dropping the moment they open a playlist.
 *
 * Each underlying layout supplies its own single `<main>`, so the page body
 * passed here must stay layout-neutral (no page-owned `<main>`).
 */
export function AuthedShell({
  children,
  headerVariant = "light",
}: AuthedShellProps) {
  const { visitor } = usePage<SharedData>().props

  return visitor.authenticated ? (
    <DashboardLayout>{children}</DashboardLayout>
  ) : (
    <PublicLayout headerVariant={headerVariant}>{children}</PublicLayout>
  )
}
